import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import EditDrawer from '../components/EditDrawer';
import AddDrawer from '../components/AddDrawer';
import API_BASE_URL from '../config';

function Links() {
  const [links, setLinks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    if (!token) {
      // wait until user is logged in to fetch
      return;
    }
    fetch(`${API_BASE_URL}/lids`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => setLinks(data))
      .catch((error) => console.error('Error fetching links:', error));
  }, [token]); // Re-run when token changes so the app fetches when auth is present

  const handleEditClick = (link) => {
    setSelectedLink(link);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedLink(null);
  };

  const handleAddClick = () => {
    setIsAddOpen(true);
  };

  const handleAddClose = () => {
    setIsAddOpen(false);
  };

  const handleSave = (updatedLink) => {
    setIsSaving(true);
    // Ensure cap is a number
    const payload = { ...updatedLink, cap: updatedLink.cap === '' || updatedLink.cap === null ? 0 : Number(updatedLink.cap) };
    fetch(`${API_BASE_URL}/lids/${updatedLink.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (!response.ok) {
          if (response.status === 409) {
            const text = await response.text();
            if (text.includes('api_identifier')) {
              alert('The API Identifier is already in use. Please use a unique identifier.');
            } else {
              alert('Conflict detected while saving.');
            }
            throw new Error('Conflict');
          }
          throw new Error('Failed to save link');
        }
        return response.json();
      })
      .then((savedLink) => {
        // Update the link in the local state
        setLinks(links.map((link) => (link.id === savedLink.id ? savedLink : link)));
        handleDrawerClose();
      })
      .catch((error) => {
        console.error('Error saving link:', error);
        // Optionally, show an error message to the user here
      })
      .finally(() => setIsSaving(false));
  };

  const handleAddSave = (newLink) => {
    setIsAdding(true);
    const payload = { ...newLink, cap: newLink.cap === '' || newLink.cap === null ? 0 : Number(newLink.cap) };
    // If user supplied an ID, check it doesn't already exist to avoid overwriting
    if (payload.id) {
      // Basic validation of the ID format (alphanumeric, dash, underscore)
      const idRegex = /^[A-Za-z0-9_-]+$/;
      if (!idRegex.test(payload.id)) {
        alert('Invalid Link ID. Only letters, numbers, underscore and dash are allowed.');
        setIsAdding(false);
        return;
      }
      fetch(`${API_BASE_URL}/lids/${payload.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      })
        .then((response) => {
          if (response.ok) {
            // Link exists
            alert('Link ID already exists. Please choose a different ID.');
            setIsAdding(false);
            throw new Error('ID exists');
          }
          if (response.status === 404) {
            // Not found -> safe to add
            return null;
          }
          // unexpected
          throw new Error('Failed to validate ID');
        })
        .then(() => {
          return fetch(`${API_BASE_URL}/lids`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          });
        })
        .then(async (response) => {
          if (!response.ok) {
            if (response.status === 409) {
              const text = await response.text();
              if (text.includes('api_identifier')) {
                alert('The API Identifier is already in use. Please use a unique identifier.');
              } else {
                alert('Link ID already exists. Please choose a different ID.');
              }
              throw new Error('Conflict');
            }
            throw new Error('Failed to add link');
          }
          return response.json();
        })
        .then((savedLink) => {
          setLinks((current) => [savedLink, ...current]);
          handleAddClose();
        })
        .catch((error) => {
          if (error.message !== 'ID exists') {
            console.error('Error adding link:', error);
            alert('Error adding link. See console for details.');
          }
        })
        .finally(() => setIsAdding(false));

      return;
    }
    fetch(`${API_BASE_URL}/lids`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (!response.ok) {
          if (response.status === 409) {
            const text = await response.text();
            if (text.includes('api_identifier')) {
              alert('The API Identifier is already in use. Please use a unique identifier.');
            } else {
              alert('Link ID already exists. Please choose a different ID.');
            }
            throw new Error('Conflict');
          }
          throw new Error('Failed to add link');
        }
        return response.json();
      })
      .then((savedLink) => {
        setLinks((current) => [savedLink, ...current]);
        handleAddClose();
      })
      .catch((error) => {
        console.error('Error adding link:', error);
      })
      .finally(() => setIsAdding(false));
  };

  const handleDelete = (id) => {
    if (!id) return;
    const confirmed = window.confirm('Are you sure you want to delete this link? This is not reversible.');
    if (!confirmed) return;
    setIsDeletingId(id);
    fetch(`${API_BASE_URL}/lids/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })
      .then((response) => {
        if (response.status === 204 || response.ok) {
          // remove from local state
          setLinks((current) => current.filter((link) => link.id !== id));
          // close drawer if currently editing this link
          if (selectedLink && selectedLink.id === id) {
            handleDrawerClose();
          }
        } else if (response.status === 404) {
          throw new Error('Link not found');
        } else {
          throw new Error('Failed to delete link');
        }
      })
      .catch((error) => {
        console.error('Error deleting link:', error);
        alert('Failed to delete the link. See console for details.');
      })
      .finally(() => setIsDeletingId(null));
  };

  const filteredLinks = links.filter((link) => {
    const searchTermLower = searchTerm.toLowerCase();
    // Check if search term is in any of the link's properties
    return (
      link.id?.toLowerCase().includes(searchTermLower) ||
      link.destinationURL?.toLowerCase().includes(searchTermLower) ||
      link.cap?.toString().toLowerCase().includes(searchTermLower) ||
      link.publisher?.toLowerCase().includes(searchTermLower) ||
      link.api_identifier?.toLowerCase().includes(searchTermLower)
    );
  });

  return (
    <>
      <div className="page-header">
        <div className="search" style={{ flex: '1', maxWidth: '400px' }}>
          <input
            type="search"
            placeholder="Search in links..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div>
          <button className="btn primary" onClick={handleAddClick}>Add Link</button>
        </div>
      </div>
      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Link ID</th>
              <th>API Identifier</th>
              <th>Destination URL</th>
              <th>Cap</th>
              <th>Publisher</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLinks.map((link) => (
              <tr key={link.id}>
                <td>{link.id}</td>
                <td>{link.api_identifier}</td>
                <td>{link.destinationURL}</td>
                <td>{link.cap}</td>
                <td>{link.publisher}</td>
                <td>
                  <button className="action-btn" onClick={() => handleEditClick(link)}>
                    &#9998; {/* Edit Icon */}
                  </button>
                  <button
                    className="action-btn"
                    onClick={() => handleDelete(link.id)}
                    disabled={isDeletingId === link.id}
                    title="Delete link"
                    style={{ marginLeft: 8 }}
                  >
                    &#128465; {/* Trash Icon */}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isDrawerOpen && (
        <EditDrawer
          link={selectedLink}
          onClose={handleDrawerClose}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}
      {isAddOpen && (
        <AddDrawer onClose={handleAddClose} onSave={handleAddSave} isSaving={isAdding} />
      )}
    </>
  );
}

export default Links;